/**
 * The watchlist builder's rules, as plain functions.
 *
 * The builder reads as one sentence: "Matches headlines that mention any of
 * these keywords, or are about any of these tickers, or are tagged with any
 * of these", with an or | and switch between the boxes. That is the server's
 * own rule, `keywords keywordOp (tickers filterMode tags)`, so what is drawn
 * is what runs. These functions decide what goes in each box, which switches
 * show, and what a switch saves as.
 *
 * Its own module, importing only types: these are the rules worth testing,
 * and the hooks and components they serve reach `window` on import.
 */

export type Op = "AND" | "OR";
export type Box = "keywords" | "tickers" | "tags";

/** Splits typed or pasted text into entries: commas, semicolons, tabs, lines. */
export function splitEntries(text: string): string[] {
  return text
    .split(/[\n\r,;\t]+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

// ── Ticker entries ─────────────────────────────────────────────────────

/**
 * An entry the Tickers box holds back for the user to check, rather than
 * guessing.
 *
 * - "name": it reads as a name or a word — "William", "Apple Inc", "tariffs".
 * - "unknown": short and lower-case, so it could be either — "oil", "fed" —
 *   and no list the builder knows has it as a ticker.
 */
export interface TickerCheck {
  word: string;
  reason: "name" | "unknown";
}

export type TickerEntry =
  | { kind: "ticker"; symbol: string }
  | ({ kind: "check" } & TickerCheck);

/** What a symbol can look like: AAPL, BRK.B, VOD.L, 7203.T, EURUSD, ES1. */
const SYMBOL_SHAPE =
  /^(?:[A-Za-z][A-Za-z0-9]{0,5}(?:[./-][A-Za-z0-9]{1,4})?|[0-9]{3,6}(?:\.[A-Za-z]{1,3})?)$/;

/** Long enough that nobody means a symbol by it. */
const MAX_ENTRY_LENGTH = 30;

/**
 * Reads one entry typed or pasted into the Tickers box. No lookup: a symbol
 * goes in as written, upper-cased.
 *
 * The box used to look every entry up and file what came back, which was tag
 * matching in the wrong place — "William" came back as WMH.LN, a ticker tag's
 * label, and a bookmaker nobody asked for. Now only what can't be read as a
 * symbol is held back:
 *
 * - anything with a space, or too long or oddly shaped for a symbol;
 * - a capitalised word, "William" or "Apple", unless it is a known ticker;
 * - a short lower-case word that isn't a known ticker, "oil" or "fed", which
 *   is asked about rather than assumed either way.
 *
 * Capitals are taken as meant: "OIL" is the symbol, whatever lists hold.
 * `isKnown` answers from the tickers already in lists the builder has loaded
 * (tickerSuggestions.ts), so "nvda" goes straight in as NVDA.
 */
export function classifyTickerEntry(
  raw: string,
  isKnown: (symbol: string) => boolean
): TickerEntry | null {
  // A cashtag is the symbol with a marker on it.
  const word = raw.trim().replace(/^\$(?=\S)/, "");
  if (!word) return null;
  const symbol = word.toUpperCase();

  if (/\s/.test(word) || word.length > MAX_ENTRY_LENGTH) {
    return { kind: "check", word, reason: "name" };
  }
  if (!/[a-z]/.test(word)) {
    return /[A-Z0-9]/.test(word)
      ? { kind: "ticker", symbol }
      : { kind: "check", word, reason: "name" };
  }
  if (isKnown(symbol)) return { kind: "ticker", symbol };
  if (/^[A-Z][a-z]+$/.test(word) || !SYMBOL_SHAPE.test(word)) {
    return { kind: "check", word, reason: "name" };
  }
  return { kind: "check", word, reason: "unknown" };
}

export interface TickerEntries {
  tickers: string[];
  checks: TickerCheck[];
}

/**
 * Adds entries to the Tickers box: symbols to the list, the rest up for a
 * check. Nothing is added twice — symbols by value, checks by word in any
 * case — and paste order holds.
 */
export function withTickerEntries(
  current: TickerEntries,
  entries: string[],
  isKnown: (symbol: string) => boolean
): TickerEntries {
  const tickers = [...current.tickers];
  const checks = [...current.checks];
  for (const entry of entries) {
    const read = classifyTickerEntry(entry, isKnown);
    if (!read) continue;
    if (read.kind === "ticker") {
      if (!tickers.includes(read.symbol)) tickers.push(read.symbol);
      continue;
    }
    const lower = read.word.toLowerCase();
    if (checks.some((c) => c.word.toLowerCase() === lower)) continue;
    checks.push({ word: read.word, reason: read.reason });
  }
  return { tickers, checks };
}

/** Adds keywords as typed, skipping any already there in another case. */
export function withKeywords(current: string[], entries: string[]): string[] {
  const next = [...current];
  for (const entry of entries) {
    const word = entry.trim();
    if (!word) continue;
    const lower = word.toLowerCase();
    if (next.some((k) => k.toLowerCase() === lower)) continue;
    next.push(word);
  }
  return next;
}

// ── Where a picked list shows ──────────────────────────────────────────

/** What a list holds, by the box each kind of term belongs in. */
export interface ListContent {
  keywords: boolean;
  tickers: boolean;
  tags: boolean;
}

/**
 * The boxes a picked list's chip shows in: one for each kind of term it
 * holds, so a list of tickers and keywords shows in both. Its tags ride
 * along unseen unless tags are all it holds. An empty list sits with the
 * tickers, as the matchers file it.
 */
export function boxesFor(content: ListContent): Box[] {
  const boxes: Box[] = [];
  if (content.keywords) boxes.push("keywords");
  if (content.tickers) boxes.push("tickers");
  if (boxes.length === 0) boxes.push(content.tags ? "tags" : "tickers");
  return boxes;
}

/**
 * Which switches a picked list keeps at "or".
 *
 * A list is the union of its parts, and stays that way only while no AND
 * separates them. Tickers and tags meet at the tickers/tags switch, so a
 * list holding both keeps that one at "or". Keywords meet the others at the
 * keywords switch, above the tickers/tags one, so a list holding keywords
 * with anything else keeps both. Typed tickers and typed keywords are kept
 * apart (see listStorage.ts), so they never split.
 */
export function switchesHeldBy(content: ListContent): {
  keywordOp: boolean;
  filterMode: boolean;
} {
  const keywordsWithMore = content.keywords && (content.tickers || content.tags);
  return {
    keywordOp: keywordsWithMore,
    filterMode: keywordsWithMore || (content.tickers && content.tags),
  };
}

// ── The switches ───────────────────────────────────────────────────────

export interface SwitchInput {
  /** Whether each box holds anything: typed entries, picked lists or tags. */
  filled: Record<Box, boolean>;
  keywordOp: Op;
  filterMode: Op;
  /** The first picked list keeping each switch at "or" — see switchesHeldBy. */
  heldBy?: { keywordOp?: string; filterMode?: string };
}

export interface SwitchState {
  /** Both sides hold something, so the switch means something. */
  applies: boolean;
  /** Shown as a working switch. */
  shown: boolean;
  /** The list keeping it at "or", when one does. */
  heldBy?: string;
  /** What it saves as: what's set while it's shown, otherwise "or". */
  value: Op;
}

export interface RuleShape {
  keywordOp: SwitchState;
  filterMode: SwitchState;
  /**
   * Tickers and Tags drawn together, captioned, when the two switches
   * disagree — the only time the server joining them first changes anything.
   * "either": keywords AND (tickers OR tags). "both": keywords OR (tickers
   * AND tags).
   */
  frame?: "either" | "both";
  /** An AND is in force, so the list filters a panel on its own. */
  usesAnd: boolean;
}

/**
 * The switches between the boxes: which show, which a picked list holds at
 * "or", and what each saves as. A switch with an empty side saves as "or"
 * rather than whatever it was last left at, so an idle AND can't bite later
 * when something is added to the other side elsewhere (mergeCombinedFilters).
 */
export function ruleShape(input: SwitchInput): RuleShape {
  const { filled } = input;
  const make = (applies: boolean, set: Op, heldBy?: string): SwitchState => {
    const shown = applies && !heldBy;
    return {
      applies,
      shown,
      heldBy: applies ? heldBy : undefined,
      value: shown ? set : "OR",
    };
  };
  const keywordOp = make(
    filled.keywords && (filled.tickers || filled.tags),
    input.keywordOp,
    input.heldBy?.keywordOp
  );
  const filterMode = make(
    filled.tickers && filled.tags,
    input.filterMode,
    input.heldBy?.filterMode
  );
  const frame =
    keywordOp.shown && filterMode.applies && keywordOp.value !== filterMode.value
      ? keywordOp.value === "AND"
        ? "either"
        : "both"
      : undefined;
  return {
    keywordOp,
    filterMode,
    frame,
    usesAnd: keywordOp.value === "AND" || filterMode.value === "AND",
  };
}

// ── The swamping warning ───────────────────────────────────────────────

/** A chip in the Tags box, with whether it names only companies. */
export interface TagsBoxEntry {
  name: string;
  /**
   * Broad: a topic, place or asset class rather than a company. Unknown kinds
   * count as not broad, so the warning never fires on a guess.
   */
  broad: boolean;
}

/**
 * The one line under "either of these" when a broad tag sits beside tickers
 * under keywords AND: "Headlines tagged Equities count here even without a
 * Core Holdings ticker."
 *
 * Tickers and tags are OR'd, so under the AND a broad tag lets through every
 * themed headline it's on, and the tickers never narrow anything. A company
 * tag names the same companies as the tickers, so it only adds coverage. The
 * tags an included list brings are its own coverage, and aren't shown here.
 * It warns; it never blocks.
 */
export function swampingHint(input: {
  frame: RuleShape["frame"];
  /** Typed tickers in the Tickers box. */
  typedTickers: number;
  /** Names of the lists showing in the Tickers box. */
  tickerLists: string[];
  tags: TagsBoxEntry[];
}): string | undefined {
  if (input.frame !== "either") return undefined;
  if (input.typedTickers + input.tickerLists.length === 0) return undefined;
  const broad = input.tags.filter((t) => t.broad).map((t) => t.name);
  if (broad.length === 0) return undefined;
  const subject =
    input.typedTickers === 0 && input.tickerLists.length === 1
      ? `${indefinite(input.tickerLists[0])} ${input.tickerLists[0]} ticker`
      : "one of these tickers";
  return `Headlines tagged ${joinOr(broad)} count here even without ${subject}.`;
}

function indefinite(word: string): string {
  return /^[aeiou]/i.test(word) ? "an" : "a";
}

function joinOr(names: string[]): string {
  if (names.length <= 2) return names.join(" or ");
  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
}

// ── Tags ───────────────────────────────────────────────────────────────

/**
 * What a tag is, in a word a reader knows. Undefined for ticker tags, which
 * the Tags box leaves out: headlines carry the company tag, and a ticker
 * tag's label (WMH.LN) is not a symbol anyone types.
 */
export function tagKindLabel(category: string | undefined): string | undefined {
  switch (category) {
    case "ticker":
      return undefined;
    case "company":
      return "company";
    case "person":
      return "person";
    case "organisation":
      return "organisation";
    case "country":
    case "region":
    case "geographical":
      return "place";
    case "commodity":
      return "commodity";
    case "currency":
    case "fx_pair":
      return "currency";
    case "index":
      return "index";
    case "sector":
      return "sector";
    default:
      return "topic";
  }
}

/** Whether a tag of this kind is broad for swampingHint. */
export function isBroadTag(category: string | undefined): boolean {
  return category !== undefined && category !== "company" && category !== "ticker";
}

/** One tag-search hit, reduced to what matching a keyword needs. */
export interface TagHit {
  id: number;
  category?: string;
  labels: { name: string; primary?: boolean }[];
}

/**
 * The tag a keyword names exactly, if any: "opec" names OPEC. Offered as a
 * one-click "+ OPEC" under the Tags box, never added by itself — a tag is
 * matched by id, not by text, so it is something the user chooses. Ticker
 * tags are left out, as everywhere in the Tags box.
 */
export function exactTagFor(
  keyword: string,
  hits: TagHit[]
): { id: number; name: string; category?: string } | undefined {
  const wanted = keyword.trim().toLowerCase();
  if (!wanted) return undefined;
  for (const hit of hits) {
    if (hit.category === "ticker") continue;
    if (!hit.labels.some((l) => l.name.trim().toLowerCase() === wanted)) continue;
    const primary = hit.labels.find((l) => l.primary) ?? hit.labels[0];
    return { id: hit.id, name: primary.name, category: hit.category };
  }
  return undefined;
}
